/**
 * McCabe-Thiele usuli: ish chiziqlari, minimal flegma, Fenske, zinapoya.
 *
 * MANBA: Kasatkin, gl. XVII §§ "Расчёт числа тарелок", "Минимальное флегмовое число";
 *        Pavlov, Romankov, Noskov, gl. 7 (misollar 7.1–7.6);
 *        McCabe, Smith, Harriott, Ch. 21 (McCabe-Thiele, Fenske eq. 21.42);
 *        Perry's 8th ed., Sec. 13 "Binary distillation".
 *
 * TENGLAMALAR (mol ulushlari, mol/s):
 *   Kuchaytirish ish chizig'i:  y = R/(R+1)·x + xD/(R+1)
 *   q-chiziq:                   y = q/(q−1)·x − xF/(q−1)         (q = 1 → x = xF vertikal)
 *   Chiqarish ish chizig'i:     (xW, xW) va (xInt, yInt) orqali, xInt — ish chizig'i va
 *                               q-chiziq kesishmasi. Nishab L'/V' = (L + qF)/(V − (1−q)F).
 *   Minimal flegma (pinch q-chiziq ∩ muvozanat egri chizig'ida (xp, yp)):
 *       Rmin = (xD − yp)/(yp − xp)
 *   Fenske (to'liq flegma, o'zgarmas α):
 *       Nmin = ln[ (xD/(1−xD)) · ((1−xW)/xW) ] / ln α
 *   Zinapoya: y1 = xD (to'liq kondensator), xn = x*(yn), y(n+1) = ishChizig'i(xn).
 *
 * ASSUMPTION:
 *   1. Ekvimolyar bug'lanish (CMO): L va V har seksiyada o'zgarmas.
 *   2. To'liq kondensator (y1 = xD), qaynatgich — bitta nazariy bosqich.
 *   3. Rmin faqat q-chiziq ∩ muvozanat pinch bilan; tangensial pinch (egri chizig'ida
 *      burilish nuqtasi bo'lgan noideal sistemalar) hisobga olinmagan — Raoult va o'zgarmas α
 *      uchun egri chiziq botiq, tangensial pinch bo'lmaydi.
 *   4. Oxirgi bosqich kasr sifatida chiziqli interpolyatsiya qilinadi:
 *      frac = (x(n−1) − xW)/(x(n−1) − xn).
 *   5. Design rejimida oziqlanish bosqichi optimal: liquid xn ≤ xInt bo'lgan birinchi bosqich.
 */
import type { BinaryVle } from './vle';
import type { XY } from './types';

export interface OperatingLines {
  R: number;
  q: number;
  xD: number;
  xW: number;
  xF: number;
  /** Ish chiziqlari va q-chiziq kesishmasi */
  xInt: number;
  yInt: number;
  /** Chiqarish chizig'i nishabi L'/V' */
  strippingSlope: number;
  rect(x: number): number;
  strip(x: number): number;
}

export function operatingLines(
  R: number,
  q: number,
  xD: number,
  xW: number,
  xF: number,
): OperatingLines {
  const rect = (x: number): number => (R / (R + 1)) * x + xD / (R + 1);
  let xInt: number;
  if (Math.abs(q - 1) < 1e-12) {
    xInt = xF;
  } else {
    const denom = q / (q - 1) - R / (R + 1);
    xInt = (xD / (R + 1) + xF / (q - 1)) / denom;
  }
  const yInt = rect(xInt);
  const strippingSlope = xInt === xW ? Infinity : (yInt - xW) / (xInt - xW);
  const strip = (x: number): number => xW + strippingSlope * (x - xW);
  return { R, q, xD, xW, xF, xInt, yInt, strippingSlope, rect, strip };
}

/** q-chiziq va muvozanat egri chizig'i kesishmasi (pinch nuqtasi) */
export function pinchPoint(vle: BinaryVle, xF: number, q: number): XY {
  if (Math.abs(q - 1) < 1e-12) return { x: xF, y: vle.y(xF) };
  const qline = (x: number): number => (q / (q - 1)) * x - xF / (q - 1);
  const g = (x: number): number => vle.y(x) - qline(x);
  // q > 1 → pinch xF dan o'ngda; q < 1 → chapda (fayl boshidagi tahlil)
  const [a, b] = q > 1 ? [xF, 1] : [0, xF];
  const r = bisect(g, a, b);
  return { x: r, y: vle.y(r) };
}

/** Oddiy yarimlash [a, b] oralig'ida, ildiz bo'lmasa chegaraga yaqin qiymat */
function bisect(g: (x: number) => number, a: number, b: number): number {
  let lo = a;
  let hi = b;
  let glo = g(lo);
  for (let i = 0; i < 80; i++) {
    const mid = 0.5 * (lo + hi);
    const gm = g(mid);
    if (glo * gm <= 0) hi = mid;
    else {
      lo = mid;
      glo = gm;
    }
    if (hi - lo < 1e-12) break;
  }
  return 0.5 * (lo + hi);
}

/** Minimal flegma soni; pinch yaroqsiz bo'lsa Infinity */
export function minimumReflux(vle: BinaryVle, xD: number, xF: number, q: number): number {
  const p = pinchPoint(vle, xF, q);
  const denom = p.y - p.x;
  if (!(denom > 0)) return Infinity;
  const Rmin = (xD - p.y) / denom;
  return Rmin < 0 ? 0 : Rmin;
}

/** Fenske: minimal nazariy bosqichlar (qaynatgich bilan) */
export function fenskeMinimumStages(alpha: number, xD: number, xW: number): number {
  if (!(alpha > 1)) return Infinity;
  const sep = (xD / (1 - xD)) * ((1 - xW) / xW);
  return Math.log(sep) / Math.log(alpha);
}

export interface SteppingOptions {
  /** Maksimal bosqichlar (pinch aniqlash uchun) */
  maxStages: number;
  /** rating: qat'iy oziqlanish bosqichi; design: undefined → optimal */
  feedStage?: number;
}

export interface SteppingResult {
  /** Kasr bosqichlar soni; pinch bo'lsa Infinity */
  stagesExact: number;
  /** Oziqlanish bosqichi (1 dan). Pinch bo'lsa oxirgi tayinlangan yoki 0 */
  feedStage: number;
  /** Zinapoya nuqtalari */
  stairs: XY[];
  pinched: boolean;
  /** Oziqlanish bosqichi optimal joydami (rating uchun diagnostika) */
  feedStageOptimal: boolean;
}

/**
 * Zinapoya: xD dan pastga xW gacha. Har bosqichda muvozanat (gorizontal) va ish
 * chizig'i (vertikal) qadamlari.
 */
export function stepStages(
  vle: BinaryVle,
  lines: OperatingLines,
  opts: SteppingOptions,
): SteppingResult {
  const { xD, xW, xInt } = lines;
  const stairs: XY[] = [{ x: xD, y: xD }];
  let y = xD;
  let xPrev = xD;
  let feedStage = 0;
  let feedStageOptimal = true;
  for (let n = 1; n <= opts.maxStages; n++) {
    const xn = vle.x(y);
    stairs.push({ x: xn, y });
    if (xn <= xW) {
      const frac = xPrev > xn ? Math.min(1, (xPrev - xW) / (xPrev - xn)) : 1;
      stairs.push({ x: xn, y: xn });
      if (feedStage === 0) {
        // xW ga xInt dan oldin yetildi — oziqlanish eng pastda
        feedStage = n;
        feedStageOptimal = opts.feedStage === undefined;
      }
      return { stagesExact: n - 1 + frac, feedStage, stairs, pinched: false, feedStageOptimal };
    }
    if (xn >= xPrev - 1e-13) {
      return { stagesExact: Infinity, feedStage, stairs, pinched: true, feedStageOptimal };
    }
    const optimalStrip = xn <= xInt;
    const useStrip = opts.feedStage === undefined ? optimalStrip : n >= opts.feedStage;
    if (feedStage === 0 && useStrip) {
      feedStage = n;
      // optimal: bu bosqichda x birinchi marta xInt dan pastga tushgan
      feedStageOptimal = optimalStrip && xPrev > xInt;
    }
    y = useStrip ? lines.strip(xn) : lines.rect(xn);
    if (y > 1) y = 1;
    if (y < 0) y = 0;
    stairs.push({ x: xn, y });
    xPrev = xn;
  }
  return { stagesExact: Infinity, feedStage, stairs, pinched: true, feedStageOptimal };
}
